// Lightweight analytics layer. Pushes every business event to
// `window.dataLayer` (compatible with GTM and Meta Pixel) and forwards to
// PostHog when the user has granted consent via CookieBanner.
//
// Usage:
//   import { track } from "@/lib/analytics";
//   track("cta_click", { cta: "hero-buy", location: "home" });
//
// Events are buffered in-memory until consent is granted so nothing is lost
// when the banner is still visible.

const CONSENT_KEY = "csm_consent_v1";
const DEV = typeof process !== "undefined" && process.env.NODE_ENV !== "production";
const BUFFER = [];

function hasConsent() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(CONSENT_KEY) === "granted";
  } catch (err) {
    if (DEV) {
      // localStorage blocked (iOS private / disabled cookies) → treat as no consent.
      // eslint-disable-next-line no-console
      console.warn("Analytics: hasConsent fallback (localStorage blocked)", err);
    }
    return false;
  }
}

function pushDataLayer(event) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

function pushPostHog(name, props) {
  if (typeof window === "undefined") return;
  if (!hasConsent()) return;
  if (window.posthog && typeof window.posthog.capture === "function") {
    try {
      window.posthog.capture(name, props || {});
    } catch {
      /* ignore */
    }
  }
}

export function track(name, props = {}) {
  const event = {
    event: name,
    ...props,
    ts: Date.now(),
  };
  // dataLayer always receives events (non-identifying, analytics-neutral).
  pushDataLayer(event);

  if (hasConsent()) {
    pushPostHog(name, props);
  } else {
    BUFFER.push({ name, props });
  }
}

function flushBuffer() {
  if (!hasConsent()) return;
  while (BUFFER.length) {
    const q = BUFFER.shift();
    pushPostHog(q.name, q.props);
  }
}

export function onConsentGranted() {
  // Called by CookieBanner when user accepts. Replays buffered events into
  // PostHog and emits a single `consent_granted` event.
  flushBuffer();
  track("consent_granted", {});
}

export function onConsentDenied() {
  // Clear buffer — we don't persist events that the user rejected.
  BUFFER.length = 0;
  pushDataLayer({ event: "consent_denied", ts: Date.now() });
}

export function hasAnalyticsConsent() {
  return hasConsent();
}
